import { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import { AxiosError } from 'axios';
import { cn, useMediaQuery } from 'common/utils';
import {
  colorByValue,
  errorMessageByCode,
  fixedNumber,
  formatCurrency,
} from 'common/utils/trading-demo';
import { CloseIcon } from 'components/v2/atoms/icon';
import { Input } from 'components/v2/atoms/input';
import { useMutation, useQuery } from 'react-query';
import {
  callGetPriceInfoRequest,
  callSettleRequest,
  PortfolioPositionDetailType,
  PositionTypeEnum,
} from 'service/trading-demo';
import useGlobalStore from 'store/global';

import { SettleSuccessPopupType } from './settle-success-popup';

interface SettleDialogPropsType {
  position: PortfolioPositionDetailType | null;
  onClose: () => void;
  onSettleSuccess: () => void;
  setSuccessPopupData: (data: SettleSuccessPopupType | null) => void;
}

const SettleDialog = ({
  position,
  onClose,
  onSettleSuccess,
  setSuccessPopupData,
}: SettleDialogPropsType) => {
  const [showCalculationDetails, setShowCalculationDetails] = useState(false);
  const setSnackbarMessage = useGlobalStore(
    (state) => state.setSnackbarMessage,
  );
  const currentAuth = useGlobalStore((state) => state.currentAuth);
  const isAuthInit = useGlobalStore((state) => state.isAuthInit);
  const { data: priceInfoSettleData } = useQuery(
    [
      'priceInfoDataSettle',
      currentAuth?.uid?.toString(),
      position?.symbol_id?.toString(),
    ],
    async () => {
      if (!currentAuth || !isAuthInit) return Promise.reject();
      return await callGetPriceInfoRequest({
        symbolId: position?.symbol_id,
      });
    },
    {
      enabled: !!currentAuth && isAuthInit,
      refetchOnWindowFocus: false,
    },
  );

  const { mutate: handleSettleAction, isLoading } = useMutation(
    async ({
      positionId,
      quantity,
    }: {
      positionId: number;
      quantity: number;
    }) => {
      return await callSettleRequest({
        positionId: positionId,
        quantity: quantity,
      });
    },
    {
      onError: (error: AxiosError<{ error: { detail_code: string } }>) => {
        const errorMessage =
          errorMessageByCode(error?.response?.data?.error?.detail_code || '') ||
          '';
        setSnackbarMessage({
          type: 'error',
          text: errorMessage,
        });
      },
      onSuccess: () => {
        // Show success popup with settle details
        setSuccessPopupData({
          symbol: position.symbol_name,
          qty: Number(position.quantity),
          positionType: position.position_type,
        });
        onClose();
        setTimeout(() => {
          onSettleSuccess();
        }, 1000);
      },
    },
  );

  const isMobile = useMediaQuery('(max-width: 767px)');
  const jpyRate = parseFloat(priceInfoSettleData?.data?.jpy_rate);
  const currency = priceInfoSettleData?.data?.currency || '';
  const symbolPrice = priceInfoSettleData?.data?.price || '';
  const symbolName = position?.symbol_name || '';

  if (!position) return null;

  const handleSettle = () => {
    if (Number(position.quantity) <= 0) {
      alert('決済数量が無効です');
      return;
    }

    handleSettleAction({
      positionId: Number(position.id),
      quantity: Number(position.quantity),
    });
  };

  // Calculate proportional P&L for settle quantity
  // const settleRatio = Number(position.quantity) / Number(position.quantity);
  // const settleProfitLoss = Number(position.unrealized_pnl) * settleRatio;

  return (
    <Dialog
      open={!!position}
      onClose={onClose}
      maxWidth="lg"
      fullWidth={isMobile}
      PaperProps={{
        style: {
          backgroundColor: 'white',
          borderRadius: '16px',
          maxHeight: 'calc(100vh - 80px)',
        },
      }}
      slotProps={{
        backdrop: {
          style: {
            backgroundColor: 'rgba(0, 0, 0, 0.30)',
          },
        },
      }}
    >
      <div className="all-font-sans w-full sm:w-[420px]">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-pp-border-primary px-4 py-5">
          <h2 className="text-base font-bold leading-none text-pp-text-primary">
            デモトレードを決済
          </h2>
          <button onClick={onClose}>
            <CloseIcon
              className="size-4 text-pp-text-secondary"
              width={16}
              height={16}
            />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Symbol Info */}
          <div className="mb-6 text-center">
            <div className="mb-2 text-2xl font-bold text-pp-text-primary">
              {symbolName}
            </div>
            <div className="text-xl text-pp-text-secondary">
              {formatCurrency({
                value: position.current_price,
                withSign: false,
                supportZero: false,
                currency: currency || '',
                isSymbolPrice: true,
              })}
            </div>
          </div>

          {/* Current Position Info */}
          <div className="mb-6 rounded-lg bg-[#F8F9FA] p-4">
            <div className="mb-2 flex items-center justify-between text-sm">
              <span className="text-pp-text-secondary">現在のポジション</span>
              <div className="flex items-center gap-2 text-pp-text-primary">
                <div
                  className={cn(
                    'flex size-6 shrink-0 items-center justify-center rounded text-xs font-bold',
                    position.position_type === PositionTypeEnum.Long
                      ? 'bg-pp-green-subtle text-growth-green'
                      : 'bg-[#ef444426] text-pp-error-primary',
                  )}
                >
                  {position.position_type === PositionTypeEnum.Long
                    ? '買'
                    : '売'}
                </div>
                <span>数量：{position.quantity}</span>
              </div>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-pp-text-secondary">デモ評価損益</span>
              <span
                className={cn(
                  'font-semibold',
                  colorByValue(
                    parseFloat(position.unrealized_pnl),
                    'text-pp-text-primary',
                  ),
                )}
              >
                {formatCurrency({
                  value: position.unrealized_pnl,
                  supportZero: true,
                  withSign: true,
                  currency: 'JPY',
                })}
              </span>
            </div>
          </div>

          {/* Settle Quantity Input */}
          <div className="mb-6">
            <label className="mb-2 block text-sm text-pp-text-primary">
              決済数量
            </label>
            <Input
              type="number"
              value={Number(position.quantity)}
              disabled={true}
              className="w-full rounded-lg border border-pp-border-primary px-4 py-3 text-center text-base font-bold leading-none focus:border-transparent focus:outline-none focus:ring-1 focus:ring-pp-text-primary"
            />
          </div>

          {/* Settlement Calculation - Collapsible */}
          <div className="mb-6 rounded-lg border border-pp-border-primary bg-[#F8F9FA]">
            {/* Summary Row (Always Visible) */}
            <div
              className="flex cursor-pointer items-center justify-between p-4"
              onClick={() => setShowCalculationDetails(!showCalculationDetails)}
            >
              <div className="flex w-full items-center justify-between">
                <span className="text-[15px] font-semibold text-pp-text-primary">
                  決済金額
                </span>
                <span className="text-lg font-bold text-pp-text-primary">
                  {formatCurrency({
                    value: String(
                      parseFloat(symbolPrice || '0') *
                        Number(position.quantity) *
                        jpyRate, // Default JPY rate
                    ),
                    supportZero: false,
                    withSign: false,
                    currency: 'JPY',
                  })}
                </span>
              </div>
              <button
                type="button"
                className={`ml-2 transition-transform duration-300 ease-in-out ${
                  showCalculationDetails ? 'rotate-180' : ''
                }`}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M4 6L8 10L12 6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>

            {/* Detailed Calculation (Collapsible) */}
            <div
              className={`overflow-hidden border-t border-pp-border-primary transition-all duration-300 ease-in-out ${
                showCalculationDetails
                  ? 'max-h-96 opacity-100'
                  : 'max-h-0 opacity-0'
              }`}
            >
              <div className="space-y-3 p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-pp-text-secondary">
                    現在価格
                  </span>
                  <span className="text-sm font-semibold text-pp-text-primary">
                    {formatCurrency({
                      value: symbolPrice,
                      withSign: false,
                      supportZero: false,
                      currency: currency || '',
                      isSymbolPrice: true,
                    })}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-pp-text-secondary">
                    決済数量
                  </span>
                  <span className="text-sm font-semibold text-pp-text-primary">
                    {position.quantity}
                  </span>
                </div>
                <div className="border-t border-pp-border-primary pt-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-pp-text-secondary">
                      小計 ({currency || 'USD'})
                    </span>
                    <span className="text-sm font-semibold text-pp-text-primary">
                      {formatCurrency({
                        value: String(
                          parseFloat(symbolPrice || '0') *
                            Number(position.quantity),
                        ),
                        withSign: false,
                        supportZero: false,
                        currency: currency || '',
                        isSymbolPrice: true,
                      })}
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between bg-white px-1 py-2">
                  <span className="flex items-center gap-1 text-sm text-pp-text-secondary">
                    為替レート
                  </span>
                  <span className="text-sm font-semibold text-pp-text-primary">
                    1 {currency || 'USD'} = {fixedNumber(jpyRate, 2)} JPY
                  </span>
                </div>
                <div className="border-t border-pp-border-primary pt-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-pp-text-primary">
                      合計 (JPY)
                    </span>
                    <span className="text-lg font-bold text-pp-text-primary">
                      {formatCurrency({
                        value: String(
                          parseFloat(symbolPrice || '0') *
                            Number(position.quantity) *
                            jpyRate, // Default JPY rate
                        ),
                        supportZero: false,
                        withSign: false,
                        currency: 'JPY',
                      })}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Profit/Loss Preview */}
          <div
            className={`mb-6 rounded-lg border px-3 py-2.5 ${
              parseFloat(position.unrealized_pnl) >= 0
                ? 'border-green-200 bg-green-50'
                : 'border-red-200 bg-red-50'
            }`}
          >
            <div className="flex items-center gap-2">
              <div className="flex size-6 items-center justify-center">
                {parseFloat(position.unrealized_pnl) >= 0 ? (
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <circle
                      cx="8"
                      cy="8"
                      r="7"
                      stroke="#00A143"
                      strokeWidth="2"
                    />
                    <path
                      d="M5 8L7 10L11 6"
                      stroke="#00A143"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <circle
                      cx="8"
                      cy="8"
                      r="7"
                      stroke="#EF4444"
                      strokeWidth="2"
                    />
                    <path
                      d="M5 5L11 11M11 5L5 11"
                      stroke="#EF4444"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                )}
              </div>
              <span
                className={`text-[13px] font-normal ${
                  parseFloat(position.unrealized_pnl) >= 0
                    ? 'text-green-700'
                    : 'text-red-700'
                }`}
              >
                この決済で{' '}
                {formatCurrency({
                  value: position.unrealized_pnl,
                  supportZero: true,
                  withSign: true,
                  currency: 'JPY',
                })}{' '}
                {parseFloat(position.unrealized_pnl) >= 0
                  ? 'の利益が確定します'
                  : 'の損失が確定します'}
              </span>
            </div>
          </div>

          {/* Settle Button */}
          <button
            onClick={handleSettle}
            disabled={Number(position.quantity) <= 0 || isLoading}
            className="mb-4 w-full rounded-lg bg-pp-error-primary p-4 text-base font-semibold text-white transition-colors hover:bg-red-600 disabled:cursor-not-allowed disabled:bg-gray-400"
          >
            ポジションを決済
          </button>

          {/* Notice */}
          <div className="text-center text-xs text-pp-text-secondary">
            決済すると、デモ評価損益が実現損益になります。
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default SettleDialog;
